import React from 'react'
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Box,
  Typography,
  Stack,
  IconButton,
} from '@mui/material'
import ReactTimeago from 'react-timeago'
import { Check, Check as CheckIcon, Close, Close as CloseIcon } from '@mui/icons-material'
import { ConnectionRequest } from '@/types'
import { convertToGMT } from '@/utils/helper'
import { UserAvatar } from '@/components'
import colors from '@/styles/colors'
import { RBox } from '@/pages/__styled'
import CustomTimeAgo from '@/components/CustomTimeAgo'
import { styled } from '@mui/system'
import { useManageConnections } from '@/hooks/api/dashboard/useManageConnections'
import toast from 'react-hot-toast'
import LoadingAnimatedView from '@/components/LoadingAnimatedView'

type Variant = 'list' | 'popup'

interface StyledBoxProps {
  variant: Variant
}

const CardContainer = styled(Box)<StyledBoxProps>(({ theme, variant }) => ({
  display: 'flex',
  flexDirection: 'column',

  '& .commentContainer': {
    marginTop: '8px',
    marginLeft: '40px',
    backgroundColor: '#F7F7F7',
    padding: '18px 20px',
    borderRadius: '8px',
  },

  ...(variant === 'list' && {
    '& .sender': {
      fontSize: '13px',
      fontWeight: '600',
      color: colors.themeBlack,
    },
    '& .topicTitle': {
      fontSize: '13px',
      fontFamily: 'Inter-Medium',
      color: colors.themeBlack,
    },
    '& .activity': {
      fontSize: '13px',
      fontWeight: '300',
      color: colors.darkGrey,
    },
    '& .dateTime': {
      fontSize: '10px',
      fontWeight: '400',
      color: colors.themePurpleTextColor,
    },
    '& .commentText': {
      fontSize: '13px',
      fontWeight: '400',
      style: 'italic',
      color: colors.themeBlack,
    },
  }),
  ...(variant === 'popup' && {
    '& .sender': {
      fontSize: '13px',
      fontWeight: '600',
      color: colors.themeBlack,
    },
    '& .topicTitle': {
      fontSize: '13px',
      fontFamily: 'Inter-Medium',
      color: colors.themeBlack,
    },
    '& .activity': {
      fontSize: '13px',
      fontWeight: '300',
      color: colors.darkGrey,
    },
    '& .dateTime': {
      fontSize: '10px',
      fontWeight: '400',
      color: colors.themePurpleTextColor,
    },
    '& .commentText': {
      fontSize: '13px',
      fontWeight: '400',
      style: 'italic',
      color: colors.themeBlack,
    },
    '& .accept-icon-btn': {
      backgroundColor: colors.green,
      color: 'white',
      borderRadius: 10,
      '&:hover': {
        backgroundColor: `${colors.green}AA`,
      },
    },
    '& .reject-icon-btn': {
      backgroundColor: `${colors.error}`,
      color: 'white',
      borderRadius: 10,
      '&:hover': {
        backgroundColor: `${colors.error}66`,
      },
    },
  }),
}))

export interface RequestListItemProps {
  onClick: (item: ConnectionRequest) => void
  variant?: Variant
  item: ConnectionRequest
}

const RequestListItem: React.FC<RequestListItemProps> = (props: RequestListItemProps) => {
  // const timeAgo = formatDistanceToNow(new Date(datetime), { addSuffix: true })

  const { onClick, item } = props
  const {
    rFname: firstName,
    rLname: lastName,
    profileAlbumPic,
    reqReceivedTime,
    connReqID,
    requestorID,
  } = item
  const fullName = `${firstName} ${lastName}`

  const { acceptConnectionInvite, rejectConnectionInvite } = useManageConnections()

  const onAccept = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!acceptConnectionInvite.isLoading) {
      toast.promise(
        acceptConnectionInvite.mutateAsync({
          connID: requestorID,
          connReqID: connReqID,
        }),
        {
          loading: 'Accepting Request...',
          success: <Typography variant='p'>{`${firstName} is now a connection`}</Typography>,
          error: <Typography variant='p'>{'Failed to accept follow request.'}</Typography>,
        },
      )
    }
  }
  const onReject = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!rejectConnectionInvite.isLoading) {
      toast.promise(
        rejectConnectionInvite.mutateAsync({
          connID: requestorID,
          connReqID: connReqID,
        }),
        {
          loading: 'Processing the decline...',
          success: <Typography variant='p'>{`Success!`}</Typography>,
          error: <Typography variant='p'>{'Failed to decline the request.'}</Typography>,
        },
      )
    }
  }

  return (
    <CardContainer variant={'popup'} onClick={() => onClick(item)} sx={{ cursor: 'pointer' }}>
      <RBox>
        <Box>
          <UserAvatar borderRadius={100} name={fullName} url={profileAlbumPic} size={35} />
        </Box>
        <Typography mx={1} sx={{ flex: 1 }}>
          <Typography className={'sender'} component='span' display='block'>
            {fullName}
          </Typography>
          <Typography className={'dateTime'} component='span' display='block'>
            <CustomTimeAgo date={reqReceivedTime} />
          </Typography>
        </Typography>

        {rejectConnectionInvite.isLoading || acceptConnectionInvite.isLoading ? (
          <LoadingAnimatedView showGif={false} />
        ) : (
          <RBox sx={{ gap: 2 }}>
            <IconButton onClick={onReject} className='reject-icon-btn' sx={{ marginLeft: 1 }}>
              <Close width={24} height={24} />
            </IconButton>
            <IconButton onClick={onAccept} className='accept-icon-btn'>
              <Check width={24} height={24} />
            </IconButton>
          </RBox>
        )}
      </RBox>

      {/* <RBox sx={{ justifyContent: 'space-between', mt: 1 }}>
        <Typography className={'activity'} component='span'>
          {'sent a follow request '}
          <Typography className={'dateTime'} component='span'>
            <ReactTimeago date={convertToGMT(reqReceivedTime)} />
          </Typography>
        </Typography>
      </RBox> */}

      {/* {item.eventType == SocketEventType.NOTE_COMMENT_ADDED && (
        <Box sx={{ display: 'flex' }}>
          <Box className={'commentContainer'} width={'auto'}>
            <Typography className={'commentText'}>
              {ActivityTypeUtil.getCommentText(item)}
            </Typography>
          </Box>
        </Box>
      )} */}
    </CardContainer>
  )
}

export default RequestListItem
